import { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TrackedAnswer } from "@/components/learn/TrackedAnswer";
import { InteractiveVideo } from "@/components/learn/InteractiveVideo";
import { Badge } from "@/components/ui/badge";
import { T4RWordmark } from "@/components/t4r/T4RWordmark";
import { LearnerProfile } from "@/components/t4r/LearnerProfile";
import { LessonMarkdown } from "@/components/t4r/LessonMarkdown";
import { LearnerDashboard } from "@/components/learn/LearnerDashboard";
import { CourseView, LessonSidebar } from "@/components/learn/CourseView";
import { ResourceList } from "@/components/learn/ResourceList";
import { AuthShell } from "@/components/auth/AuthShell";
import { AuthField, AuthSubmit } from "@/components/auth/AuthField";
import { LearnerAttemptCard } from "@/components/t4r/LearnerAttemptCard";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import { useBranding } from "@/contexts/BrandingContext";
import { Mail, Lock, ArrowRight, ChevronRight, BookOpen, LogOut, UserRound, Play, FileText, CheckCircle2, AlertTriangle, ChevronLeft, Loader2 } from "lucide-react";

/**
 * Learner-facing retrofit training portal.
 *
 * Totally separate from the CRM — no CRM shell, no CRM auth cookie, no
 * CRM API usage. Mounts on `/learn/*` and renders one of four views
 * depending on the state:
 *   - no session + no ?token=  → login screen
 *   - no session + ?token=     → accept-invite screen
 *   - signed in, no selection  → course list
 *   - signed in with lesson    → lesson + quiz view
 */
const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api/lms`,
  withCredentials: true,
});

export default function LearnerPortalPage() {
  const { lms_name } = useBranding();
  useDocumentTitle("Learner portal", `${lms_name} learner portal`, lms_name);
  const navigate = useNavigate();
  const location = useLocation();
  const [bootstrapped, setBootstrapped] = useState(false);
  const [learner, setLearner] = useState(null);
  const [view, setView] = useState("login"); // login | accept | courses | lesson | profile
  const [courses, setCourses] = useState([]);
  const [activeCourse, setActiveCourse] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [activeLesson, setActiveLesson] = useState(null);

  // Shared form state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [name, setName] = useState("");
  const [token, setToken] = useState("");
  const [working, setWorking] = useState(false);

  const bootstrap = useCallback(async () => {
    const params = new URLSearchParams(location.search);
    const t = params.get("token");
    if (t) {
      setToken(t);
      setView("accept");
      setBootstrapped(true);
      return;
    }
    try {
      const { data } = await api.get("/learner/me");
      setLearner(data);
      setView("courses");
    } catch (err) {
      setView("login");
    } finally {
      setBootstrapped(true);
    }
  }, [location.search]);

  useEffect(() => { bootstrap(); }, [bootstrap]);

  const loadCourses = useCallback(async () => {
    try {
      const { data } = await api.get("/learner/courses");
      setCourses(data.courses || []);
    } catch (err) { toast.error("Couldn't load your courses"); }
  }, []);

  useEffect(() => { if (view === "courses" && learner) loadCourses(); }, [view, learner, loadCourses]);

  const openCourse = async (c) => {
    try {
      const { data } = await api.get(`/learner/courses/${c.id}/lessons`);
      setActiveCourse(c);
      setLessons(data.lessons || []);
    } catch (err) { toast.error("Couldn't load lessons"); }
  };

  const openLesson = async (lessonId) => {
    try {
      const { data } = await api.get(`/learner/lessons/${lessonId}`);
      setActiveLesson(data);
      setView("lesson");
    } catch (err) { toast.error("Couldn't open lesson"); }
  };

  const logout = async () => {
    try { await api.post("/learner/logout"); } catch (_) {}
    setLearner(null); setActiveCourse(null); setActiveLesson(null); setLessons([]); setCourses([]);
    setView("login");
  };

  const doLogin = async (e) => {
    e.preventDefault(); setWorking(true);
    try {
      const { data } = await api.post("/learner/login", { email: email.trim(), password });
      setLearner(data); setPassword(""); setView("courses");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally { setWorking(false); }
  };

  const doAccept = async (e) => {
    e.preventDefault();
    if (password !== password2) { toast.error("Passwords don't match"); return; }
    if (password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setWorking(true);
    try {
      const { data } = await api.post("/learner/accept-invite", { token, password });
      setLearner(data); setPassword(""); setPassword2("");
      navigate("/learn", { replace: true });
      setView("courses");
      toast.success("Welcome in — let's get started!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not accept invite");
    } finally { setWorking(false); }
  };

  if (!bootstrapped) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="animate-spin text-slate-400" /></div>;

  // ── LOGIN / ACCEPT shells (shared chrome) ──────────────────────
  if (view === "login" || view === "accept") {
    const isAccept = view === "accept";
    return (
      <AuthShell
        testId={isAccept ? "accept-form" : "login-form"}
        cardLogo={<div className="text-center"><T4RWordmark size="lg" /><p className="mt-2 text-[11px] font-semibold uppercase tracking-[0.25em] text-green-700">Learner portal</p></div>}
        title={isAccept ? "Activate your account" : "Secure Portal Access"}
        switchLink={<>Staff member? <a href="/login" className="font-medium text-blue-600 hover:underline" data-testid="go-staff-login">Sign in to the staff portal →</a></>}
        subtitle={isAccept ? "Set a password to start your training." : "Sign in to access your training, assessments and certification."}
      >
        <form onSubmit={isAccept ? doAccept : doLogin} className="space-y-5">
          {isAccept ? (
            <>
              <AuthField label="New password" icon={Lock} type="password" value={password} onChange={(e) => setPassword(e.target.value)} required testId="accept-pw" />
              <AuthField label="Confirm password" icon={Lock} type="password" value={password2} onChange={(e) => setPassword2(e.target.value)} required testId="accept-pw2" />
            </>
          ) : (
            <>
              <AuthField label="Email address" icon={Mail} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" required testId="login-email" />
              <AuthField label="Password" icon={Lock} type="password" value={password} onChange={(e) => setPassword(e.target.value)} required testId="login-pw" />
            </>
          )}
          <AuthSubmit loading={working} testId={isAccept ? "accept-submit" : "login-submit"}>
            {working ? <Loader2 size={14} className="animate-spin" /> : <>{isAccept ? "Activate account" : "Sign in"} <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" /></>}
          </AuthSubmit>
        </form>
      </AuthShell>
    );
  }

  // ── Signed in shell ────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-20 bg-[#06142e] px-4 py-3 flex items-center justify-between text-white">
        <div className="flex items-center gap-2">
          <T4RWordmark dark />
          <span className="text-xs text-sky-100/70 ml-2 hidden sm:inline">· {learner?.name || learner?.email}</span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => { setActiveLesson(null); setActiveCourse(null); setView("courses"); }} className="h-8 text-xs gap-1 text-white hover:bg-white/10 hover:text-white" data-testid="nav-my-courses"><BookOpen size={12}/> My dashboard</Button>
          <Button variant="ghost" size="sm" onClick={() => setView("profile")} className="h-8 text-xs gap-1 text-white hover:bg-white/10 hover:text-white" data-testid="nav-profile"><UserRound size={12}/> Profile</Button>
          <Button variant="outline" size="sm" onClick={logout} className="h-8 text-xs gap-1 bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white" data-testid="logout-btn"><LogOut size={12}/> Sign out</Button>
        </div>
      </header>

      <main className={`mx-auto p-4 sm:p-6 ${view === "lesson" ? "max-w-6xl" : "max-w-5xl"}`}>
        {view === "courses" && !activeCourse && (
          <LearnerDashboard api={api} courses={courses} onOpenCourse={openCourse}
            onResume={async (r) => { await openCourse(courses.find((c) => c.id === r.course_id) || { id: r.course_id }); openLesson(r.lesson_id); }} />
        )}
        {view === "courses" && activeCourse && (
          <CourseView api={api} course={activeCourse} lessons={lessons} onBack={() => { setActiveCourse(null); loadCourses(); }} onOpen={openLesson} />
        )}
        {view === "profile" && (
          <LearnerProfile api={api} learner={learner} onUpdated={(d) => setLearner((prev) => ({ ...prev, ...d }))} onBack={() => setView("courses")} />
        )}
        {view === "lesson" && activeLesson && (
          <div className="grid gap-6 lg:grid-cols-[230px_1fr]">
            <aside className="hidden lg:block"><div className="sticky top-20"><LessonSidebar lessons={lessons} currentId={activeLesson.id} onOpen={openLesson} /></div></aside>
            <LessonView key={activeLesson.id} lesson={activeLesson} lessons={lessons} onNavigate={openLesson}
              onCompleted={() => activeCourse && openCourse(activeCourse)}
              onBack={async () => { setView("courses"); if (activeCourse) await openCourse(activeCourse); }} />
          </div>
        )}
      </main>
    </div>
  );
}

function LessonView({ lesson, lessons, onBack, onNavigate, onCompleted }) {
  const [completed, setCompleted] = useState(lesson.completed);
  const idx = lessons.findIndex((l) => l.id === lesson.id);
  const prev = idx > 0 ? lessons[idx - 1] : null;
  const next = idx >= 0 && idx < lessons.length - 1 ? lessons[idx + 1] : null;
  const markRead = async () => {
    try { await api.post(`/learner/lessons/${lesson.id}/complete`); setCompleted(true); toast.success("Lesson marked as read"); onCompleted(); }
    catch (err) { toast.error(err.response?.data?.detail || "Couldn't update"); }
  };
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const setAnswer = (qid, val) => setAnswers(prev => ({ ...prev, [qid]: val }));

  const submit = async () => {
    const quiz = lesson.quiz;
    const payload = { answers: quiz.questions.map(q => ({
      question_id: q.id,
      selected_index: q.kind === "mcq" ? (answers[q.id]?.selected_index ?? null) : null,
      text: q.kind === "written" ? (answers[q.id]?.text || "") : "",
      behaviour: q.kind === "written" ? (answers[q.id]?.behaviour || null) : null,
    })) };
    setSubmitting(true);
    try {
      const { data } = await api.post(`/learner/lessons/${lesson.id}/submit`, payload);
      setResult(data);
      onCompleted();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submit failed");
    } finally { setSubmitting(false); }
  };

  const video = lesson.video_external_url || lesson.has_stored_video;
  const interactive = lesson.video_checkpoints?.length > 0 && !/vimeo|youtube|youtu\.be/.test(lesson.video_external_url || "");
  const videoSrc = lesson.video_external_url || `${process.env.REACT_APP_BACKEND_URL}/api/lms/learner/lessons/${lesson.id}/video`;

  return (
    <div>
      <button onClick={onBack} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 mb-3"><ChevronLeft size={12}/> Back to lessons</button>
      <h2 className="font-heading text-2xl font-semibold mb-1">{lesson.title}</h2>
      <p className="mb-4 flex items-center gap-3 text-xs text-slate-500" data-testid="lesson-meta">
        <span>Lesson {idx + 1} of {lessons.length}</span><span>· {interactive ? `Video quiz · ${lesson.video_checkpoints.length} questions` : `${lesson.reading_minutes} min read`}</span>
        {completed && <span className="inline-flex items-center gap-1 font-medium text-green-700"><CheckCircle2 size={12} /> Completed</span>}
      </p>
      {interactive && <InteractiveVideo lesson={lesson} src={videoSrc} onFinished={() => { setCompleted(true); onCompleted(); }} />}
      {video && !interactive && (
        <div className="mb-4 border border-border rounded-md bg-black aspect-video overflow-hidden">
          {lesson.video_external_url ? (
            /vimeo|youtube|youtu\.be/.test(lesson.video_external_url)
              ? <iframe title={lesson.title} src={toEmbedUrl(lesson.video_external_url)} className="w-full h-full" frameBorder="0" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen />
              : <video controls src={lesson.video_external_url} className="w-full h-full" />
          ) : (
            <video controls src={`${process.env.REACT_APP_BACKEND_URL}/api/lms/learner/lessons/${lesson.id}/video`} className="w-full h-full" />
          )}
        </div>
      )}
      {lesson.body_markdown && (
        <LessonMarkdown>{lesson.body_markdown}</LessonMarkdown>
      )}
      {lesson.resources?.length > 0 && (
        <section className="mb-6" data-testid="lesson-resources">
          <h3 className="mb-2 font-heading text-base font-semibold">Course materials</h3>
          <ResourceList resources={lesson.resources} />
        </section>
      )}
      {!lesson.quiz && !interactive && (
        <div className="mb-6 flex items-center justify-between rounded-xl border border-border bg-white p-4">
          <p className="text-sm text-slate-600">{completed ? "You've completed this lesson." : "Finished reading? Mark it as read to count towards your progress."}</p>
          <button onClick={markRead} disabled={completed} className="inline-flex items-center gap-1.5 rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:bg-green-100 disabled:text-green-700" data-testid="mark-read-btn">
            <CheckCircle2 size={14} /> {completed ? "Completed" : "Mark as read"}
          </button>
        </div>
      )}
      {lesson.quiz && !result && (
        <div className="border border-border rounded-md bg-white p-4" data-testid="quiz">
          <h3 className="font-heading font-semibold mb-3">{lesson.quiz.title}</h3>
          <div className="space-y-5">
            {lesson.quiz.questions.map((q, i) => (
              <div key={q.id}>
                <p className="font-medium text-sm mb-2">{i + 1}. {q.prompt}</p>
                {q.kind === "mcq" ? (
                  <div className="space-y-1.5">
                    {q.options.map((opt, idx) => (
                      <label key={idx} className={`block text-sm px-3 py-1.5 border rounded cursor-pointer ${answers[q.id]?.selected_index === idx ? "border-green-500 bg-green-50" : "border-border hover:bg-slate-50"}`}>
                        <input type="radio" className="mr-2" checked={answers[q.id]?.selected_index === idx} onChange={() => setAnswer(q.id, { selected_index: idx })} data-testid={`q-${q.id}-opt-${idx}`} />
                        {opt}
                      </label>
                    ))}
                  </div>
                ) : (
                  <>
                    <TrackedAnswer
                      value={answers[q.id]?.text || ""}
                      onChange={(text, behaviour) => setAnswer(q.id, { text, behaviour })}
                      placeholder={q.min_words ? `Please write at least ${q.min_words} words. In your own words.` : "Your answer…"}
                      testId={`q-${q.id}-text`}
                    />
                    <p className="text-[10px] text-amber-700 mt-1 flex items-center gap-1"><AlertTriangle size={11}/>This answer is checked for AI-generated or pasted text and marked by your tutor.</p>
                  </>
                )}
              </div>
            ))}
          </div>
          <Button onClick={submit} disabled={submitting} className="mt-5 bg-green-700 hover:bg-green-800 text-white gap-1" data-testid="quiz-submit">
            {submitting ? <Loader2 size={14} className="animate-spin"/> : <FileText size={14}/>} Submit answers
          </Button>
        </div>
      )}
      {result && <LearnerAttemptCard attempt={result} testId="quiz-result" />}
      {!result && (lesson.attempts || []).length > 0 && (
        <div className="mt-6 space-y-3" data-testid="attempt-history">
          <h3 className="font-heading font-semibold">Your previous attempts</h3>
          {lesson.attempts.map((a) => <LearnerAttemptCard key={a.id} attempt={a} testId={`attempt-history-${a.id}`} />)}
        </div>
      )}
      <div className="mt-8 flex items-center justify-between border-t border-border pt-4" data-testid="lesson-nav">
        {prev ? <button onClick={() => onNavigate(prev.id)} className="flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900" data-testid="lesson-prev"><ChevronLeft size={14} /> {prev.title}</button> : <span />}
        {next && <button onClick={() => onNavigate(next.id)} className="flex items-center gap-1 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700" data-testid="lesson-next">Next: {next.title} <ChevronRight size={14} /></button>}
      </div>
    </div>
  );
}

function toEmbedUrl(url) {
  const m = url.match(/vimeo\.com\/(\d+)/);
  if (m) return `https://player.vimeo.com/video/${m[1]}`;
  const yt = url.match(/(?:youtu\.be\/|youtube\.com\/watch\?v=)([\w-]+)/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  return url;
}
